import { fieldGroupRanges } from './filterReview';

function escapeRegExp(s) {
  return s.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
}

// Related-term/plural expansion is for free-text terms only. A value scoped to a field —
// `TopMeshTopics:"crispr"` or inside a `TopMeshTopics:("crispr" OR ...)` group — is structured,
// so expanding it would change what the filter matches (mirrors LuceneTermExtractor.IsFielded
// on the backend, which never generates synonyms for fielded values in the first place; this
// guard covers the collision where the same text also appears as a bare term).
function isFieldScopedAt(str, offset, ranges) {
  return (
    /[A-Za-z_][\w.]*\s*:\s*\(?\s*$/.test(str.slice(0, offset)) ||
    ranges.some(r => r.open < offset && offset < r.close)
  );
}

/**
 * Splits `str` into quoted chunks (kept as-is) and unquoted chunks. Only unquoted
 * chunks are passed to `transform`.
 */
function replaceOutsideQuotes(str, transform) {
  const parts = [];
  let i = 0;
  while (i < str.length) {
    if (str[i] === '"') {
      const end = str.indexOf('"', i + 1);
      if (end === -1) {
        parts.push(str.slice(i));
        break;
      }
      parts.push(str.slice(i, end + 1));
      i = end + 1;
    } else {
      const nextQuote = str.indexOf('"', i);
      const unquotedEnd = nextQuote === -1 ? str.length : nextQuote;
      parts.push(transform(str.slice(i, unquotedEnd)));
      i = unquotedEnd;
    }
  }
  return parts.join('');
}

/**
 * Lucene-style enrichment only expands inside double-quoted segments. If the AI
 * returns a bare token (e.g. mouse) but synonym keys are known, quote those
 * tokens so expansion runs. Multi-word keys are applied before single-word keys
 * so sub-keys (e.g. mice inside a longer phrase) do not corrupt longer matches.
 *
 * @param {string} sourceTermString
 * @param {Record<string, string[]>} [selectedSynonymDictionary]
 * @param {Record<string, string[]>} [selectedPluralDictionary]
 * @returns {string}
 */
export function ensureQuotedTermKeysForEnrichment(
  sourceTermString,
  selectedSynonymDictionary,
  selectedPluralDictionary
) {
  if (!sourceTermString || typeof sourceTermString !== 'string') {
    return sourceTermString || '';
  }

  const keysWithExpansion = [
    ...Object.entries(selectedSynonymDictionary || {})
      .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
      .map(([k]) => k),
    ...Object.entries(selectedPluralDictionary || {})
      .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
      .map(([k]) => k),
  ];
  const unique = [...new Set(keysWithExpansion)].filter(Boolean);
  if (unique.length === 0) {
    return sourceTermString;
  }

  const multiWord = unique
    .filter(k => k.includes(' '))
    .sort((a, b) => b.length - a.length);
  const singleWord = unique
    .filter(k => !k.includes(' '))
    .sort((a, b) => b.length - a.length);

  // `(?![\w.]*\s*:)` keeps a field NAME that matches a key unquoted — both a whole name
  // (`Phase:"Phase 3"` with key "phase") and a dotted-name segment (`Organisations.keyword:"x"`
  // with key "organisations"); the offset guard keeps a field's bare VALUE (`title:cancer` with
  // key "cancer") unquoted, since fielded values must not be expanded.
  const quoteUnlessFieldScoped = (s, re) =>
    s.replace(re, (match, offset) =>
      /[A-Za-z_][\w.]*\s*:\s*\(?\s*$/.test(s.slice(0, offset))
        ? match
        : `"${match}"`
    );

  const applyMultiWord = segment => {
    let s = segment;
    for (const key of multiWord) {
      const parts = key.split(/\s+/).map(escapeRegExp);
      const re = new RegExp(`${parts.join('\\s+')}(?![\\w.]*\\s*:)`, 'gi');
      s = quoteUnlessFieldScoped(s, re);
    }
    return s;
  };

  const applySingleWord = segment => {
    let s = segment;
    for (const key of singleWord) {
      const re = new RegExp(`\\b${escapeRegExp(key)}\\b(?![\\w.]*\\s*:)`, 'gi');
      s = quoteUnlessFieldScoped(s, re);
    }
    return s;
  };

  let result = replaceOutsideQuotes(sourceTermString, applyMultiWord);
  result = replaceOutsideQuotes(result, applySingleWord);
  return result;
}

/**
 * Enriches quoted terms in the given string with synonyms.
 * Each matched term is expanded into a parenthesized OR group.
 *
 * Example (single term):
 *   Input:  `"spatial biology"`
 *   Synonyms: { "spatial biology": ["hello", "world"] }
 *   Output: `("spatial biology" OR "hello" OR "world")`
 *
 * Example (multi-term with operators):
 *   Input:  `"spatial biology" AND "gene expression"`
 *   Synonyms: { "spatial biology": ["tissue mapping"] }
 *   Output: `("spatial biology" OR "tissue mapping") AND "gene expression"`
 *
 * @param {string} sourceTermString
 * @param {Record<string, string[]>} selectedSynonymDictionary
 * @returns {string}
 */
export function enrichTermStringWithSynonyms(
  sourceTermString,
  selectedSynonymDictionary
) {
  if (!sourceTermString || !selectedSynonymDictionary) {
    return sourceTermString || '';
  }

  const normalized = ensureQuotedTermKeysForEnrichment(
    sourceTermString,
    selectedSynonymDictionary,
    null
  );

  const lowerKeyedSynonyms = Object.fromEntries(
    Object.entries(selectedSynonymDictionary).map(([key, syns]) => [
      key.toLowerCase(),
      syns,
    ])
  );

  const scopeRanges = fieldGroupRanges(normalized);

  return normalized.replace(
    /"([^"]+)"(~\d+)?/g,
    (match, term, _prox, offset) => {
      if (isFieldScopedAt(normalized, offset, scopeRanges)) {
        return match;
      }
      const synonyms = lowerKeyedSynonyms[term.toLowerCase()];
      if (!synonyms || synonyms.length === 0) {
        return match;
      }

      const quoted = synonyms.map(s => `"${s}"`);
      return `(${match} OR ${quoted.join(' OR ')})`;
    }
  );
}

/**
 * Expands quoted terms with both selected synonyms and plural variants in one OR group per term.
 *
 * @param {string} sourceTermString
 * @param {Record<string, string[]>} [selectedSynonymDictionary]
 * @param {Record<string, string[]>} [selectedPluralDictionary]
 * @returns {string}
 */
export function enrichTermStringWithSynonymsAndPlurals(
  sourceTermString,
  selectedSynonymDictionary,
  selectedPluralDictionary
) {
  if (!sourceTermString) {
    return sourceTermString || '';
  }

  const normalized = ensureQuotedTermKeysForEnrichment(
    sourceTermString,
    selectedSynonymDictionary,
    selectedPluralDictionary
  );

  const lowerSyn = selectedSynonymDictionary
    ? Object.fromEntries(
        Object.entries(selectedSynonymDictionary).map(([key, syns]) => [
          key.toLowerCase(),
          syns,
        ])
      )
    : {};

  const lowerPlur = selectedPluralDictionary
    ? Object.fromEntries(
        Object.entries(selectedPluralDictionary).map(([key, vars]) => [
          key.toLowerCase(),
          vars,
        ])
      )
    : {};

  const scopeRanges = fieldGroupRanges(normalized);

  return normalized.replace(
    /"([^"]+)"(~\d+)?/g,
    (match, term, _prox, offset) => {
      if (isFieldScopedAt(normalized, offset, scopeRanges)) {
        return match;
      }
      const key = term.toLowerCase();
      const syns = lowerSyn[key] || [];
      const plurals = lowerPlur[key] || [];
      if (syns.length === 0 && plurals.length === 0) {
        return match;
      }

      const seen = new Set([key]);
      const quotedParts = [match];
      for (const p of plurals) {
        const pk = p.toLowerCase();
        if (!seen.has(pk)) {
          seen.add(pk);
          quotedParts.push(`"${p}"`);
        }
      }
      for (const s of syns) {
        const sk = s.toLowerCase();
        if (!seen.has(sk)) {
          seen.add(sk);
          quotedParts.push(`"${s}"`);
        }
      }
      return `(${quotedParts.join(' OR ')})`;
    }
  );
}
